export type SkillProposalMode = "create" | "replace"

export type SkillBundleState = "available" | "expired"

export interface SkillProposal {
  proposalId: string
  mode: SkillProposalMode
  name: string
  description: string
  slug: string
  files: string[]
  sizeBytes: number
  bundleState: SkillBundleState
  bundle: string | null
  replacesSkillId: string | null
  sourceThreadId: string | null
}

function modeOf(raw: unknown, replacesSkillId: string | null): SkillProposalMode {
  return raw === "create" && replacesSkillId === null ? "create" : "replace"
}

function bundleStateOf(raw: unknown): SkillBundleState | null {
  if (raw === undefined || raw === null) return null
  return raw === "available" ? "available" : "expired"
}

function stringsOf(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : []
}

export function narrowSkillProposal(value: unknown, sourceThreadId: string | null): SkillProposal | null {
  if (typeof value !== "object" || value === null) return null
  const entry = value as Record<string, unknown>
  const proposalId = entry.proposal_id
  if (typeof proposalId !== "string" || typeof entry.name !== "string") return null

  const bundle = typeof entry.bundle === "string" && entry.bundle !== "" ? entry.bundle : null
  const declaredState = bundleStateOf(entry.bundle_state)
  const bundleState = declaredState ?? (bundle === null ? null : "available")
  if (bundleState === null) return null
  if (bundleState === "available" && bundle === null) return null

  const sizeBytes = entry.size_bytes
  const replacesSkillId = entry.replaces_skill_id
  const replaces = typeof replacesSkillId === "string" ? replacesSkillId : null
  return {
    proposalId,
    mode: modeOf(entry.mode, replaces),
    name: entry.name,
    description: typeof entry.description === "string" ? entry.description : "",
    slug: typeof entry.slug === "string" ? entry.slug : "",
    files: stringsOf(entry.files),
    sizeBytes: typeof sizeBytes === "number" ? sizeBytes : 0,
    bundleState,
    bundle: bundleState === "available" ? bundle : null,
    replacesSkillId: replaces,
    sourceThreadId,
  }
}

export function narrowSkillProposals(value: unknown, sourceThreadId: string | null): SkillProposal[] {
  if (!Array.isArray(value)) return []
  return value
    .map((entry) => narrowSkillProposal(entry, sourceThreadId))
    .filter((entry): entry is SkillProposal => entry !== null)
}

export interface AddedSkill {
  id: string
  resourceId: string
  name: string
  version: string
  sizeBytes: number
  sourceThreadId: string
}

export function narrowAddedSkill(value: unknown): AddedSkill | null {
  if (typeof value !== "object" || value === null) return null
  const entry = value as Record<string, unknown>
  if (typeof entry.id !== "string" || typeof entry.resourceId !== "string" || typeof entry.name !== "string") return null
  return {
    id: entry.id,
    resourceId: entry.resourceId,
    name: entry.name,
    version: typeof entry.version === "string" ? entry.version : "",
    sizeBytes: typeof entry.sizeBytes === "number" ? entry.sizeBytes : 0,
    sourceThreadId: typeof entry.sourceThreadId === "string" ? entry.sourceThreadId : "",
  }
}
